import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { stringify as qsStringify } from 'qs';
import { IStringifyOptions } from 'qs';
import { useSearchParams } from 'react-router-dom';

//MUI
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import Visibility from '@mui/icons-material/Visibility';

//common
import { ROUTES_PATH } from 'layout/constants';
import MuiTable from 'common/components/MuiTable';
import MyLink from 'common/components/MyLink';
import IconButtonTooltip from 'common/components/IconButtonTooltip';
import { jobTitles } from 'enum/combobox';
import { removeEmptyKeys } from 'common/utils';
import useUserRoles from 'common/hook/useUserRoles';

//screen
import FilterBox, { SearchParams } from 'modules/account/list/FilterBox';
import useGetAccountsList, { type Account } from 'modules/account/services/useGetAccountsList';
import { convertOrgType, convertOrgTypeToOriginal } from 'modules/account/utils';
import { AccountFilterModel } from 'modules/account/types';

const defaultSearchParams: SearchParams = {
  pageSize: 20,
  pageIndex: 1,
  filters: {},
};

const AccountPage = () => {
  const [params, setParams] = useState<SearchParams>(defaultSearchParams);

  const { formatDate, formatMessage } = useIntl();
  const [, setSearchParams] = useSearchParams();

  const { isAbleToAddOrEdit, viewOnlyClients } = useUserRoles();

  const {
    data: accounts,
    isLoading,
    isFetching,
  } = useGetAccountsList({
    queries: {
      pageSize: params.pageSize,
      offset: (params.pageIndex - 1) * params.pageSize,
      query: params.filters.name,
      orgType: convertOrgTypeToOriginal(params.filters.orgType || ''),
      orgId: params.filters.orgId,
      title: params.filters.title,
      inactive: Object.keys(params.filters).includes('inactive') ? !params.filters.inactive : false,
      sortAsc: params.filters.sortAsc ? !params.filters.sortAsc : null,
    },
  });

  // Use isFetching for ongoing requests, isLoading for initial load
  const showLoading = isLoading || isFetching;

  const handleChangeSorting = () => {
    setParams((old) => ({
      ...old,
      filters: { ...old.filters, sortAsc: Boolean(!old.filters.sortAsc) },
    }));
  };

  const handleChangePage = (pageIndex: number) => {
    setParams((old) => ({ ...old, pageIndex }));
  };

  const handleChangeRowPerPage = (pageSize: number) => {
    setParams((old) => ({ ...old, pageSize, pageIndex: 1 }));
  };

  const handleClearFilterParams = () => {
    setParams(defaultSearchParams);
  };

  const handleSetFilterParams = (filters: AccountFilterModel) => {
    setParams((old) => {
      const isNotSameSearchField = filters.name && filters.name !== old.filters.name;
      if (isNotSameSearchField) {
        return {
          pageIndex: 1,
          pageSize: 20,
          filters: removeEmptyKeys({ ...old.filters, ...filters }),
        };
      }

      const sameOrgType = old.filters.orgType === filters.orgType;
      if (sameOrgType) {
        return {
          ...old,
          filters: removeEmptyKeys({ ...old.filters, ...filters }),
        };
      }

      return {
        ...old,
        filters: {
          ...removeEmptyKeys({
            ...old.filters,
            ...filters,
            orgId: '',
            title: '',
          }),
        },
      };
    });
  };

  useEffect(() => {
    const options: IStringifyOptions = {
      arrayFormat: 'brackets',
      skipNulls: true,
    };

    const { filters, ...rest } = params;
    const stringifiedQueries = qsStringify({ ...rest, ...filters }, options);
    setSearchParams(stringifiedQueries, { replace: true });
  }, [params, setSearchParams]);

  return (
    <Paper className="paper-list">
      <Box className="paper-filter">
        {isAbleToAddOrEdit && (
          <MyLink to={ROUTES_PATH.account.create} sx={{ alignSelf: 'end' }}>
            <Button variant="contained" color="primary" startIcon={<AddRounded />}>
              <FormattedMessage id="add" />
            </Button>
          </MyLink>
        )}
        <FilterBox
          filters={params.filters}
          onSetFilters={handleSetFilterParams}
          onClearFilters={handleClearFilterParams}
        />
      </Box>

      <MuiTable<Account>
        data={accounts?.results ?? []}
        state={{ isLoading: showLoading }}
        onSortingChange={handleChangeSorting}
        paginationProps={{
          count: accounts?.total || 0,
          page: params.pageIndex,
          rowsPerPage: params.pageSize,
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowPerPage,
        }}
        columns={[
          {
            accessorKey: 'rowNumber',
            header: 'STT',
            Header: () => 'STT',
            size: 60,
            enableSorting: false,
            Cell: ({ row }) => {
              const rowIndex = row.index + 1; // row.index is 0-based
              const offset = (params.pageIndex - 1) * params.pageSize;
              return offset + rowIndex;
            },
          },
          {
            accessorKey: 'userAuthen.userActualManagingOrgName',
            header: 'userAuthen.userActualManagingOrgName',
            Header: () => <FormattedMessage id="account.unitName" />,
            enableSorting: true,
          },
          {
            accessorKey: 'name',
            header: 'name',
            enableSorting: true,
            Header: () => <FormattedMessage id="account.userName" />,
          },
          {
            accessorKey: 'userAuthen.userActualManagingOrgType',
            header: 'userAuthen.userActualManagingOrgType',
            Header: () => <FormattedMessage id="account.organizationType" />,
            Cell: ({ renderedCellValue }) => convertOrgType(`${renderedCellValue}`),
          },
          {
            accessorFn: (row) => row?.miscData?.JobPosition ?? '',
            id: 'miscData.JobPosition',
            header: 'miscData.JobPosition',
            Header: () => <FormattedMessage id="account.jobPosition" />,
            Cell: ({ renderedCellValue }) => {
              return renderedCellValue?.toString() || '';
            },
          },
          {
            accessorKey: 'title',
            header: 'title',
            Header: () => <FormattedMessage id="account.jobTitle" />,
            Cell: ({ renderedCellValue }) =>
              jobTitles.find(({ id }) => id === `${renderedCellValue}`)?.name || '',
          },
          {
            accessorKey: 'userAuthen.createdAt',
            header: 'userAuthen.createdAt',
            Header: () => <FormattedMessage id="account.createdAt" />,
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? formatDate(renderedCellValue.toString()) : '',
          },
          {
            accessorKey: 'userAuthen.expiredAt',
            header: 'userAuthen.expiredAt',
            Header: () => <FormattedMessage id="account.expiredAt" />,
            Cell: ({ renderedCellValue }) => {
              return renderedCellValue
                ? formatDate(`${renderedCellValue}`, { timeZone: 'UTC' })
                : formatMessage({ id: 'account.expiration.noExpiration' });
            },
          },
          {
            accessorKey: 'userAuthen.inactive',
            header: 'userAuthen.inactive',
            Header: () => <FormattedMessage id="statusAdmin" />,
            Cell: ({ renderedCellValue }) => (
              <Chip
                label={<FormattedMessage id={renderedCellValue ? 'deactive' : 'active'} />}
                variant="outlined"
                color={renderedCellValue ? 'error' : 'success'}
                size="small"
              />
            ),
          },
        ]}
        enableRowActions
        renderRowActions={({
          row: {
            original: { userAuthen, id },
          },
        }) => [
          isAbleToAddOrEdit &&
          (userAuthen ? !viewOnlyClients.includes(userAuthen.userActualManagingOrgId) : false) ? (
            <MyLink to={id.toString()}>
              <IconButtonTooltip tooltipProps={{ title: <FormattedMessage id="edit" /> }}>
                <EditRounded />
              </IconButtonTooltip>
            </MyLink>
          ) : (
            <MyLink style={{ opacity: 0 }}>
              <IconButtonTooltip tooltipProps={{ title: '' }}>
                <Visibility />
              </IconButtonTooltip>
            </MyLink>
          ),
          <MyLink to={`${id.toString()}/view-only`}>
            <IconButtonTooltip tooltipProps={{ title: <FormattedMessage id="quickView" /> }}>
              <Visibility />
            </IconButtonTooltip>
          </MyLink>,
        ]}
      />
    </Paper>
  );
};
export default AccountPage;
